import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Steps } from 'primereact/steps';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';
import GenericSurepassOutputComponent from '../output/GenericSurepassOutputComponent';

const FLOW_STEPS = [
    { label: 'Create Order' },
    { label: 'Order List' },
    { label: 'Get Status' },
];

const McaDocumentsFlowComponent = () => {
    // Three separate service IDs — one per step, each with its own credit cost
    const { createServiceId, listServiceId, statusServiceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [serviceDetails, setServiceDetails] = useState(null);
    const [fetchingService, setFetchingService] = useState(true);

    // client_id from Create Order — auto-passed to Get Status
    const [clientId, setClientId] = useState('');

    // Per-step results
    const [createResult, setCreateResult] = useState(null);
    const [listResult, setListResult] = useState(null);
    const [statusResult, setStatusResult] = useState(null);

    // Step 0 form
    const [entityId, setEntityId] = useState('');
    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!createServiceId) { setFetchingService(false); return; }
        axiosInstance.get(`/user/report-services/${createServiceId}`)
            .then((r) => { if (r.data.success) setServiceDetails(r.data.data); })
            .catch(() => setServiceDetails({ available_credits: 'N/A', credits_required: 'N/A' }))
            .finally(() => setFetchingService(false));
    }, [createServiceId]);

    const showError = (error, summary) => {
        const s = error.response?.status;
        const msgs = { 500: 'Server error. Try again.', 422: 'Check your inputs.', 429: 'Too many requests.', 400: 'Invalid input.', 404: 'No record found.' };
        let detail = msgs[s] || error.response?.data?.message || error.message || summary;
        if (s === 402) {
            const c = error.response?.data || {};
            detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
        }
        toast.current?.show({ severity: 'error', summary, detail, life: 4000 });
    };

    // ── Step 0: Create Order ─────────────────────────────────────────────────
    const doCreateOrder = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/mca/create-order', {
                entity_id: entityId.trim(),
                api_provider_service_id: parseInt(createServiceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Order creation failed');
            setCreateResult(res.data);
            // Extract client_id from response for use in Get Status
            const d = res.data?.data?.data || res.data?.data || {};
            const cid = d.client_id || d.clientId || '';
            setClientId(cid);
            toast.current?.show({ severity: 'success', summary: 'Order created', detail: 'Fetching your order list...', life: 2500 });
            setStep(1);
            // Auto-trigger order list
            doOrderList();
        } catch (err) {
            showError(err, 'Order creation failed');
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleCreateSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = {};
        if (!entityId.trim()) next.entityId = 'Entity ID (CIN / LLPIN) is required';
        if (!consent) next.consent = 'Please confirm consent before submitting';
        if (Object.keys(next).length) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">Entity ID:</span> <span className="text-900 font-medium">{entityId}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits required:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm MCA order creation',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, create order',
            rejectLabel: 'Cancel',
            accept: doCreateOrder,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    // ── Step 1: Order List ───────────────────────────────────────────────────
    const doOrderList = async () => {
        setLoading(true);
        setListResult(null);
        try {
            const res = await axiosInstance.get('/surepass/mca/order-list', {
                params: { api_provider_service_id: parseInt(listServiceId || createServiceId, 10) },
            });
            if (!res.data.success) throw new Error(res.data.message || 'Order list fetch failed');
            setListResult(res.data);
        } catch (err) {
            showError(err, 'Order list failed');
        } finally {
            setLoading(false);
        }
    };

    // ── Step 2: Get Status ───────────────────────────────────────────────────
    const doGetStatus = async (overrideClientId) => {
        const cid = overrideClientId || clientId;
        if (!cid) {
            toast.current?.show({ severity: 'warn', summary: 'No client ID', detail: 'Go back to step 1 — the order must be created first', life: 3000 });
            return;
        }
        setLoading(true);
        setStatusResult(null);
        try {
            const res = await axiosInstance.post('/surepass/mca/get-status', {
                client_id: cid,
                api_provider_service_id: parseInt(statusServiceId || createServiceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'Status fetch failed');
            setStatusResult(res.data);
        } catch (err) {
            showError(err, 'Get status failed');
        } finally {
            setLoading(false);
        }
    };

    const resetFlow = () => {
        setStep(0);
        setClientId('');
        setCreateResult(null);
        setListResult(null);
        setStatusResult(null);
        setEntityId('');
        setConsent(false);
        setErrors({});
        submittingRef.current = false;
    };

    // ── Renderers ────────────────────────────────────────────────────────────
    const renderStep0 = () => (
        <Card>
            <form onSubmit={handleCreateSubmit} noValidate>
                <div className="grid">
                    <div className="field col-12 md:col-8 lg:col-6">
                        <label htmlFor="mcaEntity" className="block font-medium text-sm mb-2">
                            Entity ID (CIN / LLPIN) <span className="text-red-500">*</span>
                        </label>
                        <IconField iconPosition="left" className="w-full">
                            <InputIcon className="pi pi-building" />
                            <InputText
                                id="mcaEntity"
                                value={entityId}
                                onChange={(e) => {
                                    setEntityId((e.target.value || '').toUpperCase());
                                    if (errors.entityId) setErrors((p) => ({ ...p, entityId: '' }));
                                }}
                                placeholder="e.g. U74999MH2020PTC123456"
                                maxLength={21}
                                spellCheck={false}
                                disabled={loading}
                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.entityId })}
                            />
                        </IconField>
                        {errors.entityId && (
                            <small className="p-error block mt-1">
                                <i className="pi pi-exclamation-circle mr-1" />{errors.entityId}
                            </small>
                        )}
                        <small className="text-500 block mt-1">CIN format: U74999MH2020PTC123456 · LLPIN format: AAA-1234</small>
                    </div>
                </div>
                <ConsentCheckbox
                    checked={consent}
                    onChange={(e) => { setConsent(e.checked); if (errors.consent) setErrors((p) => ({ ...p, consent: '' })); }}
                    error={errors.consent}
                    reportType="mca-create-order"
                />
                <div className="flex justify-content-end mt-3">
                    <Button type="submit" label="Create Order" icon="pi pi-arrow-right" iconPos="right" loading={loading} disabled={loading} size="small" />
                </div>
            </form>
        </Card>
    );

    const renderStep1 = () => (
        <div>
            {/* Create Order result */}
            {createResult && (
                <div className="mb-3">
                    <GenericSurepassOutputComponent reportData={createResult} title="MCA Order Created" icon="pi-check-circle" />
                    {clientId && (
                        <div className="mt-2 inline-flex align-items-center gap-2 surface-100 border-round p-2 px-3">
                            <i className="pi pi-key text-primary text-sm" />
                            <span className="text-600 text-xs">Client ID (auto-passed to Get Status):</span>
                            <span className="font-mono text-sm text-900 font-medium">{clientId}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Order List */}
            {loading && !listResult && (
                <Card>
                    <div className="text-center py-4">
                        <i className="pi pi-spin pi-spinner text-3xl text-primary block mb-2" />
                        <p className="text-600 text-sm">Fetching order list...</p>
                    </div>
                </Card>
            )}
            {listResult && <GenericSurepassOutputComponent reportData={listResult} title="MCA Order List" icon="pi-list" />}
            {!loading && !listResult && (
                <Card>
                    <div className="text-center py-3">
                        <p className="text-600 text-sm mb-3">Could not fetch order list. Click to retry.</p>
                        <Button label="Retry Order List" icon="pi pi-refresh" outlined onClick={doOrderList} />
                    </div>
                </Card>
            )}

            {listResult && (
                <div className="flex justify-content-between mt-3">
                    <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small" onClick={() => setStep(0)} disabled={loading} />
                    <Button label="Get Status" icon="pi pi-arrow-right" iconPos="right" size="small"
                        onClick={() => { setStep(2); doGetStatus(); }} />
                </div>
            )}
        </div>
    );

    const renderStep2 = () => (
        <div>
            {loading && !statusResult && (
                <Card>
                    <div className="text-center py-4">
                        <i className="pi pi-spin pi-spinner text-3xl text-primary block mb-2" />
                        <p className="text-600 text-sm">Fetching order status...</p>
                    </div>
                </Card>
            )}
            {statusResult && <GenericSurepassOutputComponent reportData={statusResult} title="MCA Order Status" icon="pi-building" />}
            {!loading && !statusResult && (
                <Card>
                    <div className="text-center py-3">
                        <p className="text-600 text-sm mb-3">Status check failed. Click to retry.</p>
                        <Button label="Retry" icon="pi pi-refresh" outlined onClick={() => doGetStatus()} />
                    </div>
                </Card>
            )}
            <div className="flex justify-content-between mt-3">
                <Button label="Back" icon="pi pi-arrow-left" severity="secondary" outlined size="small"
                    onClick={() => setStep(1)} disabled={loading} />
                <Button label="Start New Order" icon="pi pi-refresh" outlined size="small" onClick={resetFlow} />
            </div>
        </div>
    );

    const stepRenderers = [renderStep0, renderStep1, renderStep2];

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-briefcase"
                        title="MCA Documents Flow"
                        subtitle="Create an MCA order, view your order list, and check status — all in one guided flow"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    <Card className="mb-3">
                        <Steps model={FLOW_STEPS} activeIndex={step} readOnly className="mb-0" />
                    </Card>
                    {stepRenderers[step] && stepRenderers[step]()}
                </div>
            </div>
        </>
    );
};

export default McaDocumentsFlowComponent;
